import React from 'react'

import { ResponsiveImage } from '@syconium/little-miss-figgy'
import { mdPriorMedia } from '@syconium/little-miss-figgy'

import { gql } from '../../../../../../__generated__/graphql/catalog/gql'
import { ContentfulRichText } from '../../../../../../app/_components/pages/contentful/components/ContentfulRichText'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useResponsiveVideoSrc } from '../../../../../../lib/hooks/useResponsiveVideoSrc'
import { InlineVideoPlatforms } from '../../../../../../types/video'
import { VimeoVideo } from '../../../../VimeoVideo'

import {
  Container,
  Description,
  Disclaimer,
  ImageWrap,
  Subtitle,
  SubtitleWrapper,
  Title,
  TitleImage,
  TitleWrap,
  Video,
  VimeoContainer,
} from './styles'

import type { HeroFullBleedSectionFragmentFragment } from '../../../../../../__generated__/graphql/catalog/graphql'

const HeroImage: React.FC<{
  altText: string
  imageDesktop: string | null
  imageMobile: string | null
  isCriticalImage: boolean
}> = ({ altText, imageDesktop, imageMobile, isCriticalImage }) => {
  const imageSrc = imageDesktop ?? imageMobile

  if (!imageSrc) return null

  const srcs =
    imageDesktop && imageMobile
      ? {
          sm: imageMobile,
          md: imageDesktop,
        }
      : undefined

  return (
    <ResponsiveImage
      src={imageSrc}
      alt={altText}
      aspectRatios={{
        sm: null,
        md: null,
      }}
      loading={isCriticalImage ? 'default' : 'lazy'}
      fetchPriority={isCriticalImage ? 'high' : undefined}
      widths={{ unit: 'vw', sm: 100, md: 100 }}
      srcs={srcs}
    />
  )
}

export const HERO_FULL_BUILD_SECTION_FRAGMENT = gql(`
  fragment HeroFullBleedSectionFragment on HeroFullBleedSection {
    id
    description
    disclaimer
    imageDesktop
    imageMobile
    inlineVideoDesktop
    inlineVideoMobile
    inlineVimeoIdDesktop
    inlineVimeoIdMobile
    subtitle
    textColor
    title
    titleImage
    titleImageMobile
  }
`)

export type HeroFullBleedSectionProps = HeroFullBleedSectionFragmentFragment & {
  isCriticalImage: boolean
}

export const HeroFullBleedSection: React.FC<HeroFullBleedSectionProps> = ({
  title,
  subtitle,
  description,
  disclaimer,
  textColor,
  imageDesktop,
  imageMobile,
  inlineVideoDesktop,
  inlineVideoMobile,
  inlineVimeoIdDesktop,
  inlineVimeoIdMobile,
  isCriticalImage,
  titleImage,
  titleImageMobile,
}) => {
  const altText = title?.trim() || 'Hero section full-bleed section'
  const {
    magnolia: { general },
  } = useTranslation()

  const video = useResponsiveVideoSrc({
    inlineVideoDesktop,
    inlineVideoMobile,
    inlineVimeoIdDesktop,
    inlineVimeoIdMobile,
  })
  const hasMedia = !!(imageDesktop || imageMobile || video)

  return (
    <Container>
      <ImageWrap>
        {!video && (
          <HeroImage
            imageMobile={imageMobile}
            imageDesktop={imageDesktop}
            altText={altText}
            isCriticalImage={isCriticalImage}
          />
        )}

        {video?.platform === InlineVideoPlatforms.DEFAULT_PLAYER && (
          // TODO: Fix this auto-generated suppression.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          <Video src={video?.src} autoPlay muted playsInline loop />
        )}

        {video?.platform === InlineVideoPlatforms.VIMEO && (
          <VimeoContainer>
            <VimeoVideo
              playText={general.playVideo}
              pauseText={general.pauseVideo}
              autoplay
              lazy={!isCriticalImage}
              thumbnail={
                <HeroImage
                  imageMobile={imageMobile}
                  imageDesktop={imageDesktop}
                  altText={altText}
                  isCriticalImage={isCriticalImage}
                />
              }
              videoSrc={video.src}
            />
          </VimeoContainer>
        )}

        <TitleWrap textColor={textColor}>
          {titleImage && (
            <>
              {/* TODO: Fix this auto suppressed ESLint rule */}
              {/* eslint-disable-next-line rulesdir/require-responsive-images */}
              <picture>
                {titleImageMobile && <source media={mdPriorMedia} srcSet={titleImageMobile} />}
                <TitleImage alt={altText} src={titleImage} scaleDesktop={0.51} />
              </picture>
            </>
          )}
          <Title>{title}</Title>
          {subtitle && (
            <SubtitleWrapper>
              <Subtitle>{subtitle}</Subtitle>
            </SubtitleWrapper>
          )}
        </TitleWrap>
        {hasMedia && (
          <Disclaimer $textColor={textColor}>
            <ContentfulRichText content={disclaimer} bodySize='BodyTiny' fontWeight='600' />
          </Disclaimer>
        )}
      </ImageWrap>
      {description && <Description>{description}</Description>}
    </Container>
  )
}
